import {
  CurecallBadge,
  CurecallButton,
  CurecallModalContainer,
  CurecallTextarea,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FormEvent, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { SmsLogCard } from 'src/components/sms-modal/sms-log-card.component'
import { HRContactService } from 'src/services/hr-contact.service'
import { THRContact } from 'src/types/hr'
import { TSmsLog } from 'src/types/sms-log'
import { getErrorMessage } from 'src/utils/http.util'
import { capitalizeFirstLetter } from 'src/utils/string-formatter.util'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'

type TContactSmsLogsModalProps = Readonly<{
  contact: THRContact
  onClose: () => void
}>

export const ContactSmsLogsModal = ({ contact, onClose }: TContactSmsLogsModalProps) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()

  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState('')

  const {
    isLoading,
    error,
    data: smsLogs,
  } = useQuery({
    queryKey: ['hr-contact-sms-logs', project.id, contact.id],
    queryFn: () => HRContactService.getContactSmsLogs(project.id, contact.id),
  })

  const contactName = [contact.firstname, contact.lastname].filter(Boolean).join(' ') || contact.phone

  const contactAsPatient = {
    id: contact.id,
    firstname: contact.firstname ?? '',
    lastname: contact.lastname ?? '',
  }

  const mappedLogs: TSmsLog[] = (smsLogs ?? []).map((log) => ({
    id: log.id,
    message: log.message,
    inbound: log.inbound,
    sentAt: log.sentAt,
    deliveredAt: log.deliveredAt ?? undefined,
    status: log.status,
    source: null,
    type: 'TEXT',
  }))

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSending || !message.trim()) return

    setIsSending(true)
    setSendError('')

    try {
      await HRContactService.sendMessage(project.id, contact.id, { message })
      setMessage('')
      await queryClient.invalidateQueries({ queryKey: ['hr-contact-sms-logs', project.id, contact.id] })
      notify({
        title: t('pages.hr.campaigns.detail.sms-logs-modal.send-form.success-title'),
        message: t('pages.hr.campaigns.detail.sms-logs-modal.send-form.success-message', {
          name: contactName,
        }),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (error) {
      const errorMsg = error instanceof AxiosError ? getErrorMessage(error) : t('common.error.generic')
      setSendError(t('pages.hr.campaigns.detail.sms-logs-modal.send-form.error'))
      console.error('Failed to send SMS:', errorMsg)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      isOpen
      toggle={onClose}
      dialogClassName="!c-w-[67vw] !c-max-w-[67vw] [&>div]:c-flex [&>div]:c-flex-col"
      bodyClassName="tw-p-0"
    >
      <div className="c-pt-6 c-px-8 c-pb-4 c-border-b c-border-b-grey-200">
        <h3>
          {t('pages.hr.campaigns.detail.sms-logs-modal.title')} — {contactName}
        </h3>
      </div>

      {isLoading && <div className="loading" />}
      {error && <ErrorQueryHandler error={error} />}

      {!isLoading && !error && mappedLogs.length === 0 && (
        <p className="c-bg-grey-50 c-text-base c-w-full c-min-h-[50vh] c-text-center c-pt-36 c-px-8 c-pb-4 c-mb-0">
          {t('pages.hr.campaigns.detail.sms-logs-modal.empty')}
        </p>
      )}

      {!isLoading && !error && mappedLogs.length > 0 && (
        <div className="c-bg-grey-50 c-w-full c-overflow-y-auto c-pt-6 c-px-8 c-pb-4 c-min-h-[50vh] c-max-h-[70vh]">
          <div className="c-flex c-flex-col c-gap-2">
            {mappedLogs.map((smsLog, index) => {
              const prevLog = mappedLogs[index - 1]
              const isNewMonth =
                index === 0 ||
                (prevLog != null &&
                  (!dayjs(smsLog.sentAt).isSame(prevLog.sentAt, 'month') ||
                    !dayjs(smsLog.sentAt).isSame(prevLog.sentAt, 'year')))

              return (
                <div key={smsLog.id} className="c-flex c-flex-col">
                  {isNewMonth && (
                    <CurecallBadge
                      color={ECurecallBadgeColor.SMOOTH_PRIMARY}
                      className="c-my-3 c-self-center"
                    >
                      {capitalizeFirstLetter(dayjs(smsLog.sentAt).format('MMMM YYYY'))}
                    </CurecallBadge>
                  )}
                  <SmsLogCard smsLog={smsLog} patient={contactAsPatient} />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {(import.meta.env.VITE_ENVIRONMENT === 'dev' || import.meta.env.VITE_ENVIRONMENT === 'staging') && (
        <div className="c-px-8 c-py-4 c-border-t c-border-grey-200">
          <form onSubmit={handleSubmit} className="c-w-full c-flex c-flex-col">
            <div className="c-flex c-items-end c-gap-4">
              <div className="c-w-full">
                <CurecallTextarea
                  placeholder={t('pages.hr.campaigns.detail.sms-logs-modal.send-form.placeholder')}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value)
                    setSendError('')
                  }}
                  label={t('pages.hr.campaigns.detail.sms-logs-modal.send-form.label', {
                    name: contactName,
                  })}
                  wrapperClassName="tw-w-full"
                  textareaClassName="tw-h-16"
                  labelClassName="tw-font-semibold"
                  errorMessage={sendError}
                />
              </div>
              <CurecallButton
                appearance={ECurecallButtonAppearance.PRIMARY}
                type="submit"
                disabled={!message.trim() || isSending}
                isLoading={isSending}
                className="c-ml-auto c-text-nowrap c-mb-4"
              >
                {t('pages.hr.campaigns.detail.sms-logs-modal.send-form.button')}
              </CurecallButton>
            </div>
          </form>
        </div>
      )}
    </CurecallModalContainer>
  )
}
